import { useGetAllWorkflowsQuery, useDeleteWorkflowMutation, useDuplicateWorkflowMutation, useUpdateWorkflowStatusMutation } from "@/services/WorkFlowBuilderApi";
import { DataTable } from "@/components/ui/table/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { WorkflowResponse } from "@/app/types/workflow";
import { Button } from "@/components/ui/Button";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { useToast } from "@/components/ui/UseToast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { displayValue } from "@/utils/Helper";
import { DataTableRowActions } from "./DataTableRowAction";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export const WorkflowList = () => {
  useDocumentTitle("Workflows");

  const workflow = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).workflow ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const [updateStatus] = useUpdateWorkflowStatusMutation();
  const [duplicateWorkflow] = useDuplicateWorkflowMutation();

  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState<string>("");
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});
  const { toast } = useToast();
  const [deleteWorkflow] = useDeleteWorkflowMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<{ id: number; name: string } | null>(null);

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const { data: workflowsResponse, isLoading, refetch } = useGetAllWorkflowsQuery({
    offset: searchCriteria.offset,
    limit: searchCriteria.limit,
    search_key: inputValue || undefined,
    sort_key: searchCriteria.sort_key,
    sort_order: searchCriteria.sort_order,
    status: (filterData as any).status,
  });

  // Extract workflows array and total count from response
  const workflows = workflowsResponse && typeof workflowsResponse === 'object' && 'data' in workflowsResponse
    ? workflowsResponse.data
    : Array.isArray(workflowsResponse)
      ? workflowsResponse
      : [];

  const totalRecords = useMemo(() => {
    if (!workflowsResponse) return 0;

    if (typeof workflowsResponse === 'object' && 'filtered_count' in workflowsResponse) {
      return (workflowsResponse as any).filtered_count as number;
    }

    if (typeof workflowsResponse === 'object' && 'total_count' in workflowsResponse) {
      return (workflowsResponse as any).total_count as number;
    }

    return Array.isArray(workflows) ? workflows.length : 0;
  }, [workflowsResponse, workflows]);

  const tableData = useMemo(() => {
    return Array.isArray(workflows) ? workflows : [];
  }, [workflows]);

  const handleDelete = async (id: number, name: string) => {
    setWorkflowToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!workflowToDelete) return;

    try {
      await deleteWorkflow(workflowToDelete.id).unwrap();
      toast({
        variant: "success",
        title: "Success",
        description: "Workflow deleted successfully!",
      });
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete workflow. Please try again.",
      });
    } finally {
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const columns: ColumnDef<WorkflowResponse>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="ID"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[200px] truncate overflow-hidden">
                {row.original.name}
              </p>
            </TooltipTrigger>
            <TooltipContent>{row.original.name}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "execution_count",
      header: "Execution Count",
      cell: ({ row }) => {
        // return (
        //   <Tooltip>
        //     <TooltipTrigger>
        //       <p className="text-nowrap max-w-[250px] truncate overflow-hidden">
        //         {row.original.execution_count}
        //       </p>
        //     </TooltipTrigger>
        //     <TooltipContent>{row.original.execution_count}</TooltipContent>
        //   </Tooltip>
        // );

        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {
              <button
                onClick={() => { window.location.href = `/admin/workflow-executions?id=${row.original.id}` }}
                className="underline"
              >
                {displayValue(row.original.execution_count)}
              </button>
            }
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status || "draft";
        return (
          <Badge
            className={`${status === "active"
              ? "bg-green-600"
              : status === "inactive"
                ? "bg-red-500"
                : "bg-gray-500"
              } text-white font-medium`}
          >
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created At"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string;
        return date ? format(new Date(date), "yyyy-MM-dd") : "-";
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DataTableRowActions
          access={workflow}
          data={row.original}
          refetch={refetch}
          updateStatus={updateStatus}
          duplicateWorkflow={duplicateWorkflow}
          handleEdit={() => {
            window.location.href = `/admin/workflow-builder?id=${row.original.id}`;
          }}
        />
      ),
    },
  ];

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "draft", label: "Draft" },
        { value: "archived", label: "Archived" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  return (
    <div className="w-full p-5">
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete workflow "{workflowToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-start gap-2">
            <AlertDialogCancel className="w-24 m-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="w-24 bg-red-600 hover:bg-red-700 m-0"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="pt-4">
        <div className="flex flex-row lg:items-center justify-between gap-4 px-3">
          <TableSearchFilter
            inputValue={inputValue}
            setInputValue={setInputValue}
            setSearchCriteria={setSearchCriteria}
            placeHolder={"Search workflows"}
          />

          <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
            <Button
              className="bg-primary text-sm text-nowrap text-white flex items-center gap-1 lg:mb-0 h-8 px-2"
              onClick={() => navigate("/admin/workflow-builder")}
            >
              <Plus className="w-4 h-4" />
              Create New
            </Button>

            <button
              className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
              onClick={() => setOpenFilter(true)}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="capitalize">click to apply filter</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </button>
          </div>
        </div>

        <div className="mt-4 bg-white">
          <DataTable
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            searchMessage="No workflows found"
          />
        </div>

        {tableData.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )}

        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={setSearchCriteria}
          filterDisplay={filterDisplay}
          setInputValue={setInputValue}
        />
      </Card>
    </div>
  );
};
